import {
    ApiQueryEndpoints,
    ApiQuery,
    QueryResponse,
    SortDirections,
    FilterMetadata,
    FilterOperators,
} from "~/modules/filters/types";
import {
    ApiQueryFetcher,
    ApiQueryStateManager,
    ApiSearchLayout,
} from "~/modules/filters/components/ApiSearchLayout";
import { ExportTableNames } from "~/modules/downloads/types";
import { SearchControlModes } from "~/modules/filters/components/FilterControls/types";
import { Platform } from "../..";
import { Asset, AssetColumns } from "~/modules/assets/types";
import { AssetTable } from "~/modules/assets/components/AssetTable";
import { ColumnMetadata } from "~/components/DataGrid";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import { NETWORKS_COLUMN } from "~/modules/assets/components/AssetTable/columns/Networks";
import { HOLDING_ADDRESS_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/Holders";
import { ASSET_CLASS_COLUMN } from "~/modules/assets/components/AssetTable/columns/AssetClass";
import { INCORPORATION_COUNTRY_COLUMN } from "~/modules/assets/components/AssetTable/columns/IncorporationCountry";
import { LICENSE_OR_REGULATORY_FRAMEWORK } from "~/modules/assets/components/AssetTable/columns/RegulatoryFramework";
import { CONTRACT_ADDRESS_COLUMN } from "~/modules/assets/components/AssetTable/columns/ContractAddress";
import { TOKENIZATION_TYPE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TokenizationType";
import { ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE } from "~/modules/assets/components/AssetTable/columns/AssetName";
import { CIRCULATING_ASSET_VALUE_DOLLAR_COLUMN } from "~/modules/assets/components/AssetTable/columns/CirculatingAssetValueDollar";
import { NET_ASSET_VALUE_COLUMN } from "~/modules/assets/components/AssetTable/columns/NetAssetValue";
import {
    PERFORMANCE_1_MONTH_COLUMN,
    PERFORMANCE_1_YEAR_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/Performance";

// // // //

const PLATFORM_ASSETS_TABLE_COLUMNS: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
>[] = [
    ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE,
    CIRCULATING_ASSET_VALUE_DOLLAR_COLUMN,
    NET_ASSET_VALUE_COLUMN,
    PERFORMANCE_1_MONTH_COLUMN,
    PERFORMANCE_1_YEAR_COLUMN,
    HOLDING_ADDRESS_COUNT_COLUMN,
    ASSET_CLASS_COLUMN,
    TOKENIZATION_TYPE_COLUMN,
    NETWORKS_COLUMN,
    INCORPORATION_COUNTRY_COLUMN,
    LICENSE_OR_REGULATORY_FRAMEWORK,
    // ISSUER_COLUMN,
    // UNDERLYING_ASSETS_COLUMN,
    // SP_RATING_COLUMN,
    // ACCOUNT_SEGREGATION_COLUMN,
    // BANKRUPCY_REMOTE_COLUMN,
    // CUSTODY_DESCRIPTION_COLUMN,
    // AUDITOR_COLUMN,
    // TRANSPARENCY_COLUMN,
    // MONTHLY_ACTIVE_ADDRESSES_COLUMN,
    // MONTHLY_TRANSFER_VOLUME_COLUMN,
    // MONTHLY_TRANSFER_COUNT_COLUMN,
    CONTRACT_ADDRESS_COLUMN,
];

// // // //

export function PlatformAssets(props: {
    platform: Platform;
    assetsQuery: ApiQuery;
    filterMetadata: FilterMetadata[];
}) {
    const { assetsQuery, platform } = props;

    const initialState: QueryResponse<Asset> = {
        results: [],
        sort: {
            field: AssetColumns.circulatingAssetValueDollar,
            direction: SortDirections.desc,
        },
        pagination: {
            page: 1,
            perPage: 25,
            resultCount: 25,
            pageCount: 1,
        },
    };

    // NOTE - only include asset_class + network + issuer filters
    const filterMetadata = props.filterMetadata
        .filter((fm) =>
            [
                AssetColumns.assetClassID,
                AssetColumns.networkIDs,
                AssetColumns.issuerID,
            ].includes(fm.field as AssetColumns),
        )
        .filter((fm) => fm.selectOptions && fm.selectOptions.length > 1);

    return (
        <div className="mt-6">
            <ApiQueryStateManager
                defaultFilters={assetsQuery}
                modifyApiQuery={(apiQuery) => {
                    if (!apiQuery.filter) {
                        apiQuery.filter = {
                            operator: FilterOperators.And,
                            filters: [
                                {
                                    field: AssetColumns.protocolIDs,
                                    operator: FilterOperators.Includes,
                                    value: platform.id as number,
                                },
                            ],
                        };
                    }

                    return apiQuery;
                }}
            >
                {({ appliedQuery, internalQuery, resetQuery }) => (
                    <ApiQueryFetcher
                        appliedQuery={appliedQuery.query}
                        internalQuery={internalQuery.query}
                        endpoint={ApiQueryEndpoints.AssetV4}
                        initialState={initialState}
                    >
                        {(state) => {
                            return (
                                <ApiSearchLayout
                                    title="Assets"
                                    filterMetadata={filterMetadata}
                                    onResetQuery={() => {
                                        resetQuery();
                                    }}
                                    appliedQuery={appliedQuery}
                                    internalQuery={internalQuery}
                                    queryFetcherState={state}
                                    controls={{
                                        // NOTE - disable export + search + apiTools for now
                                        exportData: {
                                            enabled: true,
                                            exportTableName:
                                                ExportTableNames.assets_v4,
                                        },
                                        search: {
                                            enableBasic: true,
                                            enablePro: false,
                                            mode: SearchControlModes.Pro,
                                        },
                                        apiTools: {
                                            enabled: true,
                                            endpoint: ApiQueryEndpoints.AssetV4,
                                        },
                                        queryExamples: {
                                            items: [],
                                            onSelectItem: (selectedExample) => {
                                                internalQuery.update(
                                                    selectedExample.query,
                                                );
                                            },
                                        },
                                        sharing: {
                                            enabled: false,
                                            url: appliedQuery.url,
                                        },
                                    }}
                                >
                                    <AssetTable
                                        rows={
                                            state.appliedQueryReq.data.results
                                        }
                                        loading={state.appliedQueryReq.loading}
                                        paginationState={
                                            state.appliedQueryReq.data
                                                .pagination
                                        }
                                        columns={PLATFORM_ASSETS_TABLE_COLUMNS}
                                        analyticsTableName="platform-individual-assets"
                                        query={appliedQuery.query}
                                        onChangeQuery={appliedQuery.update}
                                        fixedHeader={true}
                                        fixedHeaderClassName="top-[6.8rem]"
                                    />
                                </ApiSearchLayout>
                            );
                        }}
                    </ApiQueryFetcher>
                )}
            </ApiQueryStateManager>
        </div>
    );
}
